import React, { useState, useEffect, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';

import DeviceSelectionScreen from './DeviceSelectionScreen/DeviceSelectionScreen';
import IntroContainer from '../IntroContainer/IntroContainer';
import MediaErrorSnackbar from './MediaErrorSnackbar/MediaErrorSnackbar';
import DateOfBirthScreen from './DateOfBirthScreen/DateOfBirthScreen';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { useAppState } from '../../state';

export enum Steps {
  dateOfBirthStep,
  deviceSelectionStep,
}

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

type VerificationPayload = {
  token: string;
  identity: { id: string; name: string };
  isHost: boolean;
};

export const returnRedirect = {
  schedule(returnUrl: string, redirectDomain: string, replace = window.location.replace.bind(window.location)) {
    const redirectUrl = new URL(returnUrl, `https://${redirectDomain}`);
    setTimeout(() => {
      replace(redirectUrl.toString());
    }, 3000);
  },
};

export default function PreJoinScreens() {
  const location = useLocation();
  const query = useQuery();
  const { getAudioAndVideoTracks } = useVideoContext();
  const { getToken, setError } = useAppState();
  const [verification, setVerification] = useState<VerificationPayload | null>(null);
  const [step, setStep] = useState(Steps.dateOfBirthStep);
  const [dateOfBirth, setDateOfBirth] = useState('');

  const [mediaError, setMediaError] = useState<Error>();
  const joinKey = query.get('join_key') || query.get('join');
  const verifyToken = query.get('verify_token');
  const appointmentId = query.get('appointment_id');
  const returnUrl = query.get('return');
  const isMemberFlow = Boolean(verifyToken || appointmentId || location.pathname === '/member-join');

  useEffect(() => {
    if (step === Steps.deviceSelectionStep && !mediaError) {
      getAudioAndVideoTracks().catch(error => {
        setMediaError(error);
      });
    }
  }, [getAudioAndVideoTracks, step, mediaError]);

  useEffect(() => {
    if (verification?.token && verification?.identity) {
      setStep(Steps.deviceSelectionStep);
    }
  }, [verification]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!joinKey && !appointmentId && !verifyToken) {
      setError(new Error('Join key is missing. Please contact your administrator.'));
      return;
    }
    getToken({
      joinKey: joinKey || undefined,
      verifyToken: isMemberFlow ? verifyToken || undefined : undefined,
      appointmentId: isMemberFlow ? appointmentId || undefined : undefined,
      dateOfBirth,
    })
      .then(payload => {
        if (!payload.token || !payload.identity) {
          throw new Error('You have failed to verify your identity. Please start over.');
        }

        setVerification(payload);
      })
      .catch(err => {
        setError(err);

        if (err && typeof err === 'object' && 'preventRedirect' in err && err.preventRedirect) {
          return;
        }

        const redirectDomain = process.env.REACT_APP_REDIRECT_DOMAIN;
        if (!redirectDomain || !returnUrl) {
          return;
        }

        returnRedirect.schedule(returnUrl, redirectDomain);
      });
  };

  return (
    <IntroContainer>
      <MediaErrorSnackbar error={mediaError} />
      {step === Steps.dateOfBirthStep && (
        <DateOfBirthScreen
          input={dateOfBirth}
          updateInput={setDateOfBirth}
          handleSubmit={handleSubmit}
          isMemberFlow={isMemberFlow}
        />
      )}

      {step === Steps.deviceSelectionStep && (
        <DeviceSelectionScreen
          token={verification?.token as string}
          name={verification?.identity.name as string}
          setStep={setStep}
        />
      )}
    </IntroContainer>
  );
}
